export const delay = (ms = 200) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

export function clone<T>(value: T): T {
  if (typeof structuredClone === "function") {
    return structuredClone(value);
  }

  return JSON.parse(JSON.stringify(value)) as T;
}

/**
 * Get the API base URL for client-side fetch calls
 * Handles web wrappers (Capacitor, Electron, etc.) where relative URLs don't work
 * 
 * Priority:
 * 1. NEXT_PUBLIC_API_URL (explicit API URL)
 * 2. NEXT_PUBLIC_APP_URL (app base URL)
 * 3. Detect if we're in a web wrapper and use the actual server URL
 * 4. Use relative URLs for standard browser environments
 */
export function getApiBaseUrl(): string {
  // Check if we're in a browser environment
  if (typeof window === "undefined") {
    // Server-side: use environment variable or default
    return process.env.NEXT_PUBLIC_API_URL || process.env.NEXT_PUBLIC_APP_URL || "";
  }

  const origin = window.location.origin;
  const protocol = window.location.protocol;
  const hostname = window.location.hostname;
  const port = window.location.port;
  const userAgent = typeof navigator !== "undefined" ? navigator.userAgent : "";

  // Check if we're in a web wrapper (non-standard origins or indicators)
  // This includes Capacitor, Ionic, Electron, and other web wrappers
  const isWebWrapper = 
    protocol === "capacitor:" ||
    protocol === "file:" ||
    protocol === "ionic:" ||
    origin.includes("capacitor://") ||
    origin.includes("file://") ||
    origin.includes("ionic://") ||
    // Detect localhost with non-standard port (common in web wrappers)
    (hostname === "localhost" && port !== "" && port !== "3000" && port !== "3001") ||
    // Detect if origin doesn't match a standard web domain pattern
    (!origin.startsWith("http://") && !origin.startsWith("https://")) ||
    // Check user agent for web wrapper indicators
    userAgent.includes("Electron") ||
    userAgent.includes("Capacitor") ||
    // If we're on localhost but the app URL env var is set to production, we're likely in a wrapper
    (hostname === "localhost" && process.env.NEXT_PUBLIC_APP_URL && !process.env.NEXT_PUBLIC_APP_URL.includes("localhost"));

  // Debug logging (only in development, and only log essential info)
  if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
    // Only log if there's an issue (missing env vars when in web wrapper)
    if (isWebWrapper && !process.env.NEXT_PUBLIC_API_URL && !process.env.NEXT_PUBLIC_APP_URL) {
      console.warn("[API Client] Web wrapper detected but no API URL env vars set");
    }
  }

  // Priority 1: Explicit API URL (always use if set)
  if (process.env.NEXT_PUBLIC_API_URL) {
    const apiUrl = process.env.NEXT_PUBLIC_API_URL.trim().replace(/\/+$/, "");
    if (isWebWrapper) {
      console.log("[API Client] Using NEXT_PUBLIC_API_URL in web wrapper:", apiUrl);
    }
    return apiUrl;
  }

  // Priority 2: App URL (always use if set, especially important in web wrappers)
  // BUT: In development, prefer local API over production URL so auth and API hit the dev server
  if (process.env.NEXT_PUBLIC_APP_URL) {
    const appUrl = process.env.NEXT_PUBLIC_APP_URL.trim().replace(/\/+$/, "");
    const isLocalDevOrigin = hostname === "localhost" || hostname === "127.0.0.1";
    
    // In development, if we're on localhost or 127.0.0.1, use current origin so API calls hit local server
    // (127.0.0.1 was previously missed, so signin went to production and failed)
    if (process.env.NODE_ENV === "development" && origin && isLocalDevOrigin) {
      if (isWebWrapper) {
        console.log("[API Client] Development mode: Using local origin instead of NEXT_PUBLIC_APP_URL to avoid CSP issues");
      }
      return origin;
    }
    
    if (isWebWrapper) {
      console.log("[API Client] Using NEXT_PUBLIC_APP_URL in web wrapper:", appUrl);
    }
    return appUrl;
  }

  // Priority 3: If we detected a web wrapper but no env vars, use default
  // BUT: In development, prefer local origin (localhost or 127.0.0.1)
  if (isWebWrapper) {
    const isLocalDevOrigin = hostname === "localhost" || hostname === "127.0.0.1";
    if (process.env.NODE_ENV === "development" && origin && isLocalDevOrigin) {
      console.log("[API Client] Development mode: Using local origin as default");
      return origin;
    }
    const defaultUrl = "https://growth-sync.com";
    console.warn("[API Client] Detected web wrapper but no env vars set. Using default:", defaultUrl, {
      origin,
      protocol,
      hostname,
      port,
    });
    return defaultUrl;
  }

  // Priority 4: Standard browser - use relative URLs (empty string)
  // This allows Next.js to handle the routing correctly
  // Always use relative URLs for localhost in development
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return "";
  }
  
  return "";
}

/**
 * Build an absolute API URL from a relative path
 * @param path - Relative API path (e.g., "/api/communities")
 * @returns Absolute URL if needed, or relative path for standard browsers
 */
export function getApiUrl(path: string): string {
  const baseUrl = getApiBaseUrl();
  
  // Ensure path starts with /
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  
  // If we have a base URL, return absolute URL
  if (baseUrl) {
    return `${baseUrl}${normalizedPath}`;
  }
  
  // Otherwise, return relative path (works in standard browsers)
  return normalizedPath;
}


